// Tracks whether a scroll container has more content in each direction so UI
// can show/hide overflow affordances (fades, "scroll for more" chips). Drives
// both the vertical per-column hint and the horizontal category-grid hint, so
// the two read as one system.
//
// Recomputes on scroll (passive), on element resize (ResizeObserver), and when
// any value in `deps` changes — the last is needed because adding/removing
// items changes scrollHeight/scrollWidth without necessarily changing the
// element's own border-box (which is all ResizeObserver watches).
import { useState, useEffect, useCallback } from 'react';

// Sub-pixel epsilon so fractional scroll offsets don't flicker the affordances.
const EDGE = 1;

const useOverflow = (ref, deps = []) => {
  const [state, setState] = useState({
    canUp: false, canDown: false, canLeft: false, canRight: false,
  });

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const {
      scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth,
    } = el;
    const next = {
      canUp: scrollTop > EDGE,
      canDown: scrollTop + clientHeight < scrollHeight - EDGE,
      canLeft: scrollLeft > EDGE,
      canRight: scrollLeft + clientWidth < scrollWidth - EDGE,
    };
    setState((prev) => ((prev.canUp === next.canUp && prev.canDown === next.canDown
      && prev.canLeft === next.canLeft && prev.canRight === next.canRight)
      ? prev : next));
  }, [ref]);

  useEffect(() => {
    check();
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    el.addEventListener('scroll', check, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener('scroll', check); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, ...deps]);

  return state;
};

export default useOverflow;
